import React, { useEffect, useState, FC } from 'react';
import { documentService } from '../../../../utils/window.utils';
import Icon from '../../../@shared/icon';
import { iconSize } from '../../../../constants/icon.constants';
import ctaTypes from '../../../../enums/CTA.enums';
import Accordion, { TAccordionProps } from '../../../@shared/filters/components/accordion';
import DictionaryTerm from '../../../@shared/dictionaryTerm';
import { NewsListingModel } from '../../models/newsListing.model';
import { TNewsListingFilter, TSelectedNewsFilters } from '../../types/newsListing.types';
import { useNavigate } from 'react-router';
import { QueryParamsConst } from '../../../../constants/query-params.constants';
import { useTranslation } from 'react-i18next';
import {
  NEWS_FILTER1_FILTER_TITLE_DICTIONARY_TERM,
  NEWS_LINE_FILTER_TITLE_DICTIONARY_TERM,
  NEWS_TYPE_FILTER_TITLE_DICTIONARY_TERM,
  NEWS_YEAR_FILTER_TITLE_DICTIONARY_TERM,
} from '../../../../constants/newsListnig.constatnts';
import { googleClickDropdownEvent } from '../../../../utils/analytics';

const MobileFilter: FC<{ model: NewsListingModel; selectedFilters: TSelectedNewsFilters }> = ({ model, selectedFilters }) => {
  const [shouldOpenFilterModal, setShouldOpenFilterModal] = useState(false);
  const [selectedFiltersCopy, setSelectedFiltersCopy] = useState(selectedFilters);
  const [selectedAccordionId, setSelectedAccordionId] = useState<string | undefined>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const docElement = documentService();
    if (!docElement?.body) return;

    if (shouldOpenFilterModal) docElement.body.classList.add('body--with-filter-model');
    else docElement.body.classList.remove('body--with-filter-model');
  }, [shouldOpenFilterModal]);

  const { YEAR, TYPE, LINE, FILTER1 } = QueryParamsConst;
  // show the action CTA's if any option is selectd or change in selected option
  const shouldShowFilterWrapper =
    !(
      selectedFiltersCopy[YEAR] === selectedFilters[YEAR] &&
      selectedFiltersCopy[LINE] == selectedFilters[LINE] &&
      selectedFiltersCopy[TYPE] === selectedFilters[TYPE] &&
      selectedFiltersCopy[FILTER1] === selectedFilters[FILTER1]
    ) ||
    selectedFiltersCopy[YEAR] !== '' ||
    selectedFiltersCopy[LINE] !== '' ||
    selectedFiltersCopy[TYPE] !== '' ||
    selectedFiltersCopy[FILTER1] !== '';

  const getAccordionOptionsByInd = (filter?: TNewsListingFilter, titleDictionaryKey?: string): TAccordionProps['accordionOptions'] => {
    if (!filter) return [];
    return [
      { id: filter.value, title: t(titleDictionaryKey || '') },
      ...(filter?.tags?.map((tag) => ({ id: tag.id?.toString(), title: tag.value })) || []),
    ];
  };

  const accordionsProps = [
    {
      filter: model.findFilterByName('year'),
      queryKey: YEAR,
      id: 'year-accordion',
      titleDictionaryKey: 'cnhi-news-all-years',
      accordionTitle: <DictionaryTerm keyword={NEWS_YEAR_FILTER_TITLE_DICTIONARY_TERM} />,
      dataLayerTitle: NEWS_YEAR_FILTER_TITLE_DICTIONARY_TERM,
    },
    {
      filter: model.findFilterByName('line'),
      queryKey: LINE,
      id: 'line-accordion',
      titleDictionaryKey: 'cnhi-news-all-lines',
      accordionTitle: <DictionaryTerm keyword={NEWS_LINE_FILTER_TITLE_DICTIONARY_TERM} />,
      dataLayerTitle: NEWS_LINE_FILTER_TITLE_DICTIONARY_TERM,
    },
    {
      filter: model.findFilterByName('type'),
      queryKey: TYPE,
      id: 'type-accordion',
      titleDictionaryKey: 'cnhi-news-all-types',
      accordionTitle: <DictionaryTerm keyword={NEWS_TYPE_FILTER_TITLE_DICTIONARY_TERM} />,
      dataLayerTitle: NEWS_TYPE_FILTER_TITLE_DICTIONARY_TERM,
    },
    {
      filter: model.findFilterByName('filter1'),
      queryKey: FILTER1,
      id: 'filter1-accordion',
      titleDictionaryKey: 'cnhi-news-all-filter1',
      accordionTitle: <DictionaryTerm keyword={NEWS_FILTER1_FILTER_TITLE_DICTIONARY_TERM} />,
      dataLayerTitle: NEWS_FILTER1_FILTER_TITLE_DICTIONARY_TERM,
    },
  ];

  const revertToOldFilters = () => model.updateQueryByBulkKeys(navigate, selectedFiltersCopy);
  const toggleModal = () => {
    if (!shouldOpenFilterModal) setSelectedAccordionId(undefined);
    setShouldOpenFilterModal(!shouldOpenFilterModal);
  };

  const toggleAccordion = (accordionId: typeof selectedAccordionId) => {
    if (accordionId === selectedAccordionId) setSelectedAccordionId(undefined);
    else setSelectedAccordionId(accordionId);
  };

  return (
    <div className="news-listing-with-filter--mbl">
      <div
        className="news-filter-modal-switch"
        onClick={() => {
          toggleModal();
          revertToOldFilters();
        }}
      >
        <DictionaryTerm keyword="cnhi-news-filter-title" />
        <Icon iconName="set-nav-services-funnel" size={iconSize['dsk']} />
      </div>
      {shouldOpenFilterModal && (
        <div className="news-filter-modal">
          <div className="news-filter-modal__header">
            <span className="news-filter-modal__title">
              <DictionaryTerm keyword="cnhi-news-filter-title" />
            </span>
            <Icon
              iconName="action-close"
              color="white"
              size={iconSize['dsk']}
              onClick={() => {
                setShouldOpenFilterModal(false);
                revertToOldFilters();
              }}
            />
          </div>
          <div className="news-filter-modal__body">
            {accordionsProps.map(({ filter, id, accordionTitle, queryKey, titleDictionaryKey, dataLayerTitle }) => {
              const options = getAccordionOptionsByInd(filter, titleDictionaryKey);
              if (!options?.length || filter.hidden) return null;
              return (
                <Accordion
                  key={id}
                  accordionTitle={accordionTitle}
                  accordionOptions={options}
                  isToggleIndependent={false}
                  canShowAccordionContent={selectedAccordionId === id}
                  handleSwitchClick={() => toggleAccordion(id)}
                  handleOptionClick={(ind, event) => {
                    event?.preventDefault(); // to stop the twice trigger of event
                    model.updateQueryByKey(navigate, queryKey, ind === 0 ? '' : (options?.[ind].id as string));
                    googleClickDropdownEvent(t(dataLayerTitle), options[ind].title as string);
                  }}
                  selectedOptionInd={(filter?.tags?.findIndex((_) => _.id?.toString() === selectedFilters[queryKey]) ?? -1) + 1}
                />
              );
            })}
          </div>
          {
            // retaining as it might be used at a later time
            shouldShowFilterWrapper && (
              <div className="news-filter-modal__ctas">
                <span
                  className={`${ctaTypes['baseButton']} ${ctaTypes['primaryWhite']}`}
                  onClick={() => {
                    model.resetFilter(navigate);
                    setSelectedFiltersCopy({
                      [QueryParamsConst.PAGE]: '',
                      [QueryParamsConst.YEAR]: '',
                      [QueryParamsConst.LINE]: '',
                      [QueryParamsConst.TYPE]: '',
                      [QueryParamsConst.FILTER1]: '',
                    });
                  }}
                >
                  <DictionaryTerm keyword="cnhi-news-reset-filters" />
                </span>
                <span
                  className={`${ctaTypes['baseButton']} ${ctaTypes['primaryRed']}`}
                  onClick={() => {
                    setSelectedFiltersCopy(selectedFilters);
                    toggleModal();
                  }}
                >
                  <DictionaryTerm keyword="cnhi-news-apply-filters" />
                </span>
              </div>
            )
          }
        </div>
      )}
    </div>
  );
};

export default MobileFilter;
